import { SearchUserHit } from 'lib-shared-types-api';
import { ComponentProps, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';

import { searchApi } from 'apis/search';
import {
  HookFormSearchHit,
  HookFormSearchHitSelect,
} from 'components/hookForm';

import { FormValues } from '../composeEmailModal.types';

import { ContactItemContent } from './contactItemContent';

type ComponontPropsType = Omit<
  ComponentProps<typeof HookFormSearchHitSelect>['componentProps'],
  'getItemTitleByValue'
>;

type Props = {
  name: 'toEmail' | 'cc' | 'bcc';
  label: string;
  multiple?: boolean;
  required?: boolean;
  componentProps?: ComponontPropsType;
};

const createSearchHit = (input: string): SearchUserHit => ({
  id: 'suggestion',
  display_name: input,
  email: input,
  user_type: 'contact',
  _highlights: {},
  _inner_hits: null,
  created_at: '',
  organization_id: '',
});

const isSearchUserHit = (hit: HookFormSearchHit): hit is SearchUserHit => {
  if (hit && typeof hit === 'object' && 'email' in hit) {
    return true;
  }
  return false;
};

export const ComposeEmailFieldEmailContact = ({
  name,
  label,
  multiple = false,
  required = false,
  componentProps,
}: Props): JSX.Element => {
  const { control } = useFormContext<FormValues>();
  const getSuggestions = useCallback(
    (searchTerm: string, hits: readonly SearchUserHit[]) => {
      if (isEmail(searchTerm)) {
        const suggestedHit = createSearchHit(searchTerm);
        const existsInHits = hits.some((hit) => {
          const hitKeys = Object.keys(hit);
          // if any property of the hit matches, we consider it to exist so we don't show a duplicate hit
          for (const key of hitKeys) {
            if (hit[key] === searchTerm) {
              return true;
            }
          }
        });

        if (!existsInHits) {
          return [suggestedHit];
        }
      }

      return [];
    },
    [],
  );

  return (
    <HookFormSearchHitSelect
      control={control}
      name={name}
      label={label}
      rules={
        required
          ? {
              required: 'Please select an email address',
            }
          : undefined
      }
      componentProps={{
        ...componentProps,
        multiple,
        clearable: true,
        placeholder: 'Select or type an email address',
        equalityFn: (a, b) => a?.email === b?.email,
        getItemTitleByValue: (value) => value?.email ?? value?.name ?? '',
      }}
      getSuggestions={getSuggestions}
      endpoint={{
        options: {
          endpoint: searchApi.endpoints.readSearchUser,
          itemsSelector: ({ data: { results } }) =>
            results?.hits.filter((user) => user.email) ?? [],
        },
        initialArg: {
          limit: 20,
          sort: { key: 'name', direction: 'asc' },
          filter: {
            facets: {
              user_type: ['member', 'contact'],
            },
          },
        },
      }}
      transformValue={(user) => {
        const name = user.display_name || user.email;
        return {
          email: user.email || '',
          name: name || '',
        };
      }}
      renderSelectionListItemContent={(user) => {
        if (!isSearchUserHit(user)) {
          return null;
        }

        return (
          <ContactItemContent
            id={user.id}
            name={user.display_name || ''}
            email={user.email || ''}
            avatarUrl={user.avatar_url}
          />
        );
      }}
    />
  );
};
